/**
 * Each deployment region runs its own control plane and keeps its own data.
 * VITE_AGENT_MANAGER_REGIONS is a JSON list: [{"id":"us","label":"US","apiBase":"https://…","streamUrl":"https://…"}].
 * Locally it defaults to the dev server.
 */
export type Region = { id: string; label: string; apiBase: string; streamUrl?: string };

/**
 * Long-running streaming endpoints (playground, test runs) live on their own
 * Function URL when deployed and on the API itself locally. Null = not
 * available in this region.
 */
export const streamEndpoint = (r: Region, path: '/v1/playground/stream' | '/v1/test-runs/stream'): string | null =>
  r.streamUrl ? `${r.streamUrl.replace(/\/$/, '')}${path}` : r.id === 'local' ? `${r.apiBase}${path}` : null;

export const playgroundStreamUrl = (r: Region): string | null => streamEndpoint(r, '/v1/playground/stream');

const parse = (): Region[] => {
  try {
    const raw = import.meta.env.VITE_AGENT_MANAGER_REGIONS as string | undefined;
    if (raw) {
      const list = JSON.parse(raw) as Region[];
      if (Array.isArray(list) && list.length) return list;
    }
  } catch {
    /* fall through */
  }
  return [{ id: 'local', label: 'Local', apiBase: 'http://localhost:8788' }];
};

export const REGIONS = parse();
const KEY = 'am.region';

export const getRegion = (): Region => {
  let id: string | null = null;
  try {
    id = localStorage.getItem(KEY);
  } catch {
    /* storage unavailable */
  }
  return REGIONS.find((r) => r.id === id) ?? REGIONS[0];
};

export const setRegion = (id: string) => {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* ignore */
  }
};
